import { useEffect, useRef, useState, useContext } from "react";
import "./ProfileSection.css";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ThemeContext } from "../context/themeContext";
import { UserContext } from "../context/UserContext";
import { saveUserHistory } from "../saveUserHistory";
import { FaPlus } from "react-icons/fa";
import { AvatarContext } from "../context/AvatarContext";
import PasswordRecoverPopup from "../auth/PasswordRecoverPopup";
import EditProfilePopup from "./EditProfilePopup";

import {
  faPalette, faLock, faFileContract, faReceipt,
  faRightFromBracket, faChevronRight, faChevronLeft,
  faMoon, faSun, faPencilAlt, faMapMarkerAlt,
  faRightToBracket
} from "@fortawesome/free-solid-svg-icons";

export default function ProfileSectionDropdown({ isOpen, onClose, anchorRef, setToggle }) {
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [submenu, setSubmenu] = useState(null);
  const [showRecover, setShowRecover] = useState(false);
  const [showEditPopup, setShowEditPopup] = useState(false);

  const { activeTheme, handleThemeChange } = useContext(ThemeContext);
  const fileInputRef = useRef(null);
  const { avatar, setAvatar, avatarColor } = useContext(AvatarContext);
  const { user, setUser } = useContext(UserContext);

  useEffect(() => {
    const handleOutside = (e) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target) &&
        !anchorRef?.current?.contains(e.target)
      ) {
        onClose?.();
        setSubmenu(null);
      }
    };
    if (isOpen && !showRecover && !showEditPopup) {
      document.addEventListener("mousedown", handleOutside);
    }
    return () => { document.removeEventListener("mousedown", handleOutside); };
  }, [isOpen, anchorRef, onClose, showRecover, showEditPopup]);

  useEffect(() => {
    let animationFrameId;

    const updatePosition = () => {
      if (isOpen && anchorRef?.current && dropdownRef.current) {
        const iconRect = anchorRef.current.getBoundingClientRect();
        const d = dropdownRef.current;
        const viewportH = window.innerHeight;
        const height = d.offsetHeight;
        let top = iconRect.top + iconRect.height / 2 - height / 2;
        if (top < 20) top = 20;
        if (top + height > viewportH - 20) top = viewportH - height - 20;

        const newLeft = iconRect.right + 12;

        setPosition((prev) => {
          if (prev.top === top && prev.left === newLeft) return prev;
          return { top, left: newLeft };
        });
      }
      animationFrameId = requestAnimationFrame(updatePosition);
    };

    if (isOpen) {
      updatePosition();
    }

    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [isOpen, anchorRef]);

  if (!isOpen) return null;

  const closeAndNavigate = async () => {
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/auth/logout`, {
        method: "POST", credentials: "include",
      });
    } catch (e) { console.error("Logout failed:", e); }
    setUser(null);
    localStorage.removeItem("scorecardData");
    localStorage.removeItem("reportAccess");
    localStorage.removeItem("reportBAccess");
    localStorage.removeItem("scorecardAccess");
    onClose(); setToggle(false); setSubmenu(null);
    navigate("/", { replace: true });
  };

  const getRelativePath = (term) => {
    try {
      if (typeof term === "string" && term.startsWith("http")) return new URL(term).pathname;
      return typeof term === "string" ? term : "/";
    } catch { return "/"; }
  };

  const handleClose = async (o) => {
    const path = getRelativePath(o);
    const fullUrl = `${process.env.REACT_APP_FRONTEND_URL}${path}`;
    await saveUserHistory(fullUrl, user?.user_id);
    if (path === '/terms' || path === '/terms-privacy-policy' || path === '/privacy-policy') {
      navigate('/privacy-policy', { replace: true }); onClose(); return;
    }
    navigate(path, { replace: true }); onClose();
  };

  const handleAvatarClick = () => { fileInputRef.current.click(); };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { alert("Please select an image file"); return; }
    const reader = new FileReader();
    reader.onloadend = () => {
      localStorage.setItem("profileAvatar", reader.result);
      setAvatar(reader.result);
    };
    reader.readAsDataURL(file);
  };

  return (
    <>
      <div
        className="profile-dropdown-container"
        ref={dropdownRef}
        style={{ top: position.top, left: position.left }}
      >
        <div className={`profile-dropdown ${submenu ? "submenu-active" : ""} ${activeTheme === "dark" ? "dark-mode" : ""}`}>

          <div className="profile-main-menu">

            <div className="profile-account-p">
              <div
                className="profile-avatar-main"
                onClick={handleAvatarClick}
                title="Upload profile picture"
                style={{ 
                  background: (user?.profile_pic || avatar) ? "transparent" : (activeTheme === "dark" ? "#ffffff" : "#000000"),
                  border: (user?.profile_pic || avatar) ? "none" : `2.5px solid ${activeTheme === "dark" ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.15)"}`
                }}
              >
                {(user?.profile_pic || avatar)
                  ? <img src={user?.profile_pic || avatar} alt="Profile" />
                  : <span 
                      className="avatar-letter"
                      style={{ color: activeTheme === "dark" ? "#000000" : "#ffffff" }}
                    >
                      {user?.name?.trim()?.[0]?.toUpperCase() || "?"}
                    </span>
                }
                <span className="avatar-plus"><FaPlus /></span>
              </div>

              <input type="file" ref={fileInputRef} accept="image/*" style={{ display: "none" }} onChange={handleFileChange} />

              <div className="profile-details-p">
                {/* Name + Edit button — clicking Edit opens popup */}
                <div className="profile-name-wrapper">
                  <h4 className="profile-name-p">
                    {user?.name ? `${user.name} Pvt. Ltd.`.slice(0, 80) : "Guest User"}
                  </h4>
                  {user && (
                    <>
                      <button
                        className="edit-name-btn-desktop"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => setShowEditPopup(true)}
                        title="Edit profile"
                      >
                        Edit
                      </button>
                      <FontAwesomeIcon
                        icon={faPencilAlt}
                        className="edit-name-icon-mobile"
                        title="Edit"
                        onClick={() => setShowEditPopup(true)}
                      />
                    </>
                  )}
                </div>

                {/* Email — display only */}
                <p className="profile-email">{user?.email || "Not logged in"}</p>
              </div>
            </div>

            <ul className="profile-menu-content">
              <li className="menu-with-arrow" onClick={() => setSubmenu("theme")}>
                <div className="menu-left">
                  <FontAwesomeIcon icon={faPalette} className="icon-main-profile" />Theme
                </div>
                <FontAwesomeIcon icon={faChevronRight} className="arrow-icon" />
              </li>
              <li onClick={() => setShowRecover(true)}>
                <FontAwesomeIcon icon={faLock} className="icon-main-profile" />Change Password
              </li>
              <li onClick={() => handleClose("/privacy-policy")}>
                <FontAwesomeIcon icon={faFileContract} className="icon-main-profile" />Terms & Conditions
              </li>
              <li onClick={() => handleClose("/orders")}>
                <FontAwesomeIcon icon={faReceipt} className="icon-main-profile" />Orders
              </li>
              <li onClick={() => handleClose("/profile/addresses")}>
                <FontAwesomeIcon icon={faMapMarkerAlt} className="icon-main-profile" />Saved Addresses
              </li>
            </ul>

            <hr className="profile-divider" />

            {user ? (
              <div className="logout-row-main" onClick={() => closeAndNavigate("/")}>
                <FontAwesomeIcon icon={faRightFromBracket} className="logout-icon" />
                <span className="logout-text">Log out</span>
              </div>
            ) : (
              <div 
                className="logout-row-main" 
                onClick={() => {
                  console.log("[DEBUG] Login row clicked, dispatching show-guest-auth event");
                  window.dispatchEvent(new CustomEvent("show-guest-auth"));
                  onClose();
                  if (setToggle) setToggle(false);
                }}
              >
                <FontAwesomeIcon icon={faRightToBracket} className="logout-icon" />
                <span className="logout-text">Log in</span>
              </div>
            )}
          </div>

          {submenu === "theme" && (
            <div className="profile-submenu slide-in">
              <div className="submenu-back" onClick={() => setSubmenu(null)}>
                <FontAwesomeIcon icon={faChevronLeft} className="back-icon" /><span>Theme</span>
              </div>
              <ul className="theme-options">
                <li onClick={() => { handleThemeChange("light"); setSubmenu(null); onClose?.(); }}>
                  <FontAwesomeIcon icon={faSun} className="icon-main-profile" />Light
                </li>
                <li onClick={() => { handleThemeChange("dark"); setSubmenu(null); onClose?.(); }}>
                  <FontAwesomeIcon icon={faMoon} className="icon-main-profile" />Dark
                </li>
              </ul>
            </div>
          )}
        </div>
      </div>

      {showRecover && <PasswordRecoverPopup onCloseAll={() => setShowRecover(false)} />}

      {/* Edit Profile Popup — name + email OTP all in one popup */}
      {showEditPopup && (
        <EditProfilePopup
          user={user}
          onClose={() => setShowEditPopup(false)}
          onSave={({ name, email }) => {
            setUser({ ...user, ...(name && { name }), ...(email && { email }) });
          }}
        />
      )}
    </>
  );
}